import { useCallback } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ConfirmationOptions {
  title: string;
  message: string;
  okButtonTitle?: string;
  cancelButtonTitle?: string;
}

export function useNativeConfirmation() {
  const isMobile = useIsMobile();

  const showConfirmation = useCallback(async (options: ConfirmationOptions): Promise<boolean> => {
    console.info('[confirmation] showConfirmation called:', options.title);
    
    // Check if we're on Capacitor (mobile app)
    const isCapacitor = typeof window !== 'undefined' && !!(window as any).Capacitor;
    
    if (isMobile && isCapacitor) {
      try {
        // Use Capacitor's Dialog plugin
        const Capacitor = (window as any).Capacitor;
        if (Capacitor?.Plugins?.Dialog) {
          console.info('[confirmation] using Capacitor dialog');
          const result = await Capacitor.Plugins.Dialog.confirm({
            title: options.title,
            message: options.message,
            okButtonTitle: options.okButtonTitle || 'OK',
            cancelButtonTitle: options.cancelButtonTitle || 'Annuler'
          });
          console.info('[confirmation] Capacitor result:', result.value);
          return result.value;
        }
      } catch (error) {
        console.error('Native dialog error:', error);
      }
    }

    // Fallback to browser confirm for web or if native fails
    console.info('[confirmation] using browser confirm');
    const result = window.confirm(`${options.title}\n\n${options.message}`);
    console.info('[confirmation] browser result:', result);
    return result;
  }, [isMobile]);

  const showAlert = useCallback(async (title: string, message: string): Promise<void> => {
    const isCapacitor = typeof window !== 'undefined' && !!(window as any).Capacitor;
    
    if (isMobile && isCapacitor) {
      try {
        const Capacitor = (window as any).Capacitor;
        if (Capacitor?.Plugins?.Dialog) {
          await Capacitor.Plugins.Dialog.alert({
            title,
            message
          });
          return;
        }
      } catch (error) {
        console.error('Native alert error:', error);
      }
    }
    
    // Fallback to browser alert
    window.alert(`${title}\n\n${message}`);
  }, [isMobile]);

  return {
    showConfirmation,
    showAlert,
    isNative: isMobile && typeof window !== 'undefined' && !!(window as any).Capacitor
  };
}